import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ReducerStates,
  Temperature,
  TimeFormat,
} from "../../helpers/constants";
import { StorageKeys } from "../../services/constants";
import { initialize } from "./asyncThunkActions";
import { IToken } from "../../services/login.services";

export interface IAppConfiguration {
  temperature: Temperature;
  timeFormat: TimeFormat;
}
export interface IAppState {
  state: ReducerStates;
  tokenObj?: IToken;
  softNotificationToggle: boolean;
  softNotificationType: string;
  config: IAppConfiguration;
  appLogging: boolean;
  environment: string;
  version: string;
}

export const initialAppState: IAppState = {
  state: ReducerStates.IDLE,
  tokenObj: undefined,
  softNotificationToggle: false,
  softNotificationType: "",
  config: { temperature: Temperature.CELSIUS, timeFormat: TimeFormat.TIME12H },
  appLogging: process.env.REACT_APP_ENVIRONMENT !== "production",
  environment: "",
  version: "",
};

const appSlice = createSlice({
  name: "app",
  initialState: initialAppState,
  reducers: {
    finishedInit(state) {
      state.state = ReducerStates.IDLE;
    },
    handledError(state) {
      state.softNotificationToggle = false;
      state.softNotificationType = initialAppState.softNotificationType;
    },
    toggleLogging(state) {
      state.appLogging = !state.appLogging;
    },
    setEnv(state, action: PayloadAction<string>) {
      state.environment = action.payload;
      localStorage.setItem(StorageKeys.ENV, action.payload);
    },
    setVer(state, action: PayloadAction<string>) {
      state.version = action.payload;
      if (action.payload === "") {
        localStorage.removeItem(StorageKeys.VERSION);
      } else {
        localStorage.setItem(StorageKeys.VERSION, action.payload);
      }
    },
    updateLocalToken(state, action: PayloadAction<IToken>) {
      state.tokenObj = action.payload;
      localStorage.setItem(StorageKeys.USER, JSON.stringify(action.payload));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initialize.pending, (state) => {
        state.state = ReducerStates.PENDING;
      })
      .addCase(initialize.fulfilled, (state, action) => {
        sessionStorage.removeItem(StorageKeys.APP_LOG);
        state.state = ReducerStates.SUCCEEDED;
        if (action.payload.env !== null) {
          state.environment = action.payload.env;
        }
        if (action.payload.version !== null) {
          state.version = action.payload.version;
        }
        if (action.payload.userToken !== null) {
          state.tokenObj = action.payload.userToken;
        }
      });
  },
});

const { reducer } = appSlice;

// Destructure and export the plain action creators
export const {
  finishedInit,
  handledError,
  toggleLogging,
  setEnv,
  setVer,
  updateLocalToken,
} = appSlice.actions;

export default reducer;
